import runtimeConfig from "../../../runtime-config";
import React from "react";
import { useSnackbar } from "notistack";
import { connect } from "react-redux";
import { withRouter, useHistory } from "react-router-dom";
import { createStructuredSelector } from "reselect";

import axios from "axios";

import {
  setBillingAddress,
  updateBillingAddress,
  resetPayment,
} from "../../../redux/payment/payment.actions";
import { selectCurrentPayment } from "../../../redux/payment/payment.selectors";

import { resetCustomer } from "../../../redux/customer/customer.actions";
import { selectCurrentCustomer } from "../../../redux/customer/customer.selectors";

import { resetCart } from "../../../redux/cart/cart.actions";
import {
  selectCartItems,
  selectCartTotal,
} from "../../../redux/cart/cart.selectors";

import { resetShipping } from "../../../redux/shipping/shipping.actions";
import { selectCurrentShipping } from "../../../redux/shipping/shipping.selectors";

import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import "./CardSectionStyles.css";
import { startCheckout, checkoutPayload, matchesBasket, clearAttempt, assertCurrentAttempt } from '../recovery/checkout-attempt';

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
};

const API_URI = runtimeConfig.REACT_APP_API_URI;

export const StripePaymentForm = ({
  customer,
  items,
  cartTotal,
  shipping,
  payment,
  resetCart,
  resetCustomer,
  resetShipping,
  resetPayment,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const totalAmountToPay = Number(cartTotal) + Number(shipping.cost);

  const [loading, setLoading] = React.useState(false);
  const [cardComplete, setCardComplete] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState(null);
  const history = useHistory();

  const stripe = useStripe();
  const elements = useElements();

  const latestPayload = React.useRef(null);
  latestPayload.current = checkoutPayload({ customer, items, cartTotal, shipping, payment }, 'stripe');
  const payload = () => latestPayload.current;

  const finishConfirmation = async (pending) => {
    const response = await axios.post(API_URI + '/stripe/' + pending.orderId + '/payment', {
      payment_id: pending.paymentId, checkout_token: pending.attempt.token,
    });
    if (response.data.status !== 'success' || response.data.payment_status !== 'succeeded') throw new Error('Payment confirmation is pending.');
    const unchanged = await matchesBasket(pending.attempt, payload);
    clearAttempt(pending.attempt);
    enqueueSnackbar('Your order has been placed successfully.', { variant: 'success' });
    if (unchanged) { resetCart(); resetCustomer(); resetShipping(); resetPayment(); }
    history.push('/order-complete');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      if (confirmation) {
        await finishConfirmation(confirmation);
        return;
      }
      if (!stripe || !elements) return;
      const { order, attempt } = await startCheckout(payload());
      assertCurrentAttempt(attempt);
      const intent = await axios.post(API_URI + '/stripe/payment-intend', {
        order_id: order.id, order_ref: order.ref, checkout_token: attempt.token,
      });
      if (intent.data.status !== 'success' || !intent.data.secret) throw new Error(intent.data.message || 'Unable to initialise card payment.');
      const current = await stripe.retrievePaymentIntent(intent.data.secret);
      if (current.error) throw current.error;
      const pending = { orderId: order.id, paymentId: current.paymentIntent.id, attempt };
      setConfirmation(pending);
      if (!['succeeded', 'processing', 'requires_capture', 'canceled'].includes(current.paymentIntent.status)) {
        assertCurrentAttempt(attempt);
        const options = current.paymentIntent.status === 'requires_payment_method' ? {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              address: { city: payment.billing_address.city, country: payment.billing_address.country,
                line1: payment.billing_address.address_line_1, line2: payment.billing_address.address_line_2,
                postal_code: payment.billing_address.postcode, state: payment.billing_address.county },
              email: customer.details.email, name: payment.billing_address.name, phone: payment.billing_address.phone,
            },
          },
        } : undefined;
        const result = await stripe.confirmCardPayment(intent.data.secret, options);
        if (result.error) {
          if (result.error.type === 'card_error' || result.error.type === 'validation_error') setConfirmation(null);
          throw result.error;
        }
      }
      await finishConfirmation(pending);
    } catch (error) {
      const response = error.response && error.response.data;
      if (response && response.payment_status === 'requires_payment_method') setConfirmation(null);
      enqueueSnackbar((response && response.message) || error.message ||
        'Your saved checkout is retained. Reload this page to check payment before paying again.', { variant: 'error' });
    } finally { setLoading(false); }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="form-group row">
          <label htmlFor="phone" className="col-sm-3 col-form-label">
            Basket total
          </label>
          <div className="col-sm-9">
            £{cartTotal ? cartTotal.toFixed(2) : "--"}
          </div>
        </div>

        <div className="form-group row">
          <label htmlFor="phone" className="col-sm-3 col-form-label">
            {shipping.method === "collection"
              ? "Collection charge"
              : "Delivery charge"}
          </label>
          <div className="col-sm-9">
            £{shipping.cost ? Number(shipping.cost).toFixed(2) : "--"}
          </div>
        </div>

        <div className="form-group row">
          <label htmlFor="phone" className="col-sm-3 col-form-label">
            <strong>Order total</strong>
          </label>
          <div className="col-sm-9">
            <strong>£{Number(totalAmountToPay).toFixed(2)}</strong>
          </div>
        </div>

        <div className="form-group row">
          <label htmlFor="phone" className="col-sm-3 col-form-label">
            <strong>Card Details</strong> <span className="text-danger">*</span>
          </label>
          <div className="col-sm-9">
            <label>
              <CardElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(event) => {
                  if (event.complete) {
                    setCardComplete(true);
                  } else {
                    setCardComplete(false);
                  }
                }}
              />
            </label>
          </div>
        </div>

        <div className="form-group row">
          <div className="offset-sm-3 col-sm-9">
            {loading ? (
              <strong>Please wait while we process your order...</strong>
            ) : (
              ""
            )}
            <button
              disabled={
                (!confirmation && (!stripe || !payment.set_billing_address || !cardComplete)) || loading
              }
              className="btn btn-blue mt-2"
            >
              {loading ? "Please wait ..." : confirmation ? "Retry order confirmation" : "Place order and pay"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  customer: selectCurrentCustomer,
  items: selectCartItems,
  cartTotal: selectCartTotal,
  shipping: selectCurrentShipping,
  payment: selectCurrentPayment,
});

const mapDispatchToProps = (dispatch) => ({
  setBillingAddress: (address) => dispatch(setBillingAddress(address)),
  updateBillingAddress: () => dispatch(updateBillingAddress()),
  resetCart: () => dispatch(resetCart()),
  resetCustomer: () => dispatch(resetCustomer()),
  resetShipping: () => dispatch(resetShipping()),
  resetPayment: () => dispatch(resetPayment()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(StripePaymentForm)
);
